/**
 * Dati di fatturazione del tenant, obbligatori prima di qualsiasi acquisto
 * (abbonamenti e ricariche crediti AI).
 *
 * Le regole seguono la fatturazione italiana:
 * - privato → nome, cognome e codice fiscale (16 caratteri, con carattere
 *   di controllo verificato);
 * - società → ragione sociale, partita IVA (11 cifre, checksum verificato)
 *   e recapito per la fattura elettronica: codice SDI (7 caratteri) oppure PEC;
 * - per i paesi esteri i vincoli fiscali italiani (CF/SDI/checksum, CAP a 5
 *   cifre, sigla provincia) sono allentati.
 */
import { z } from "zod";

// ── codice fiscale: struttura + carattere di controllo ──────────────

/** Valori dei caratteri in posizione dispari (1-based) per il check del CF. */
const CF_ODD: Record<string, number> = {
  "0": 1, "1": 0, "2": 5, "3": 7, "4": 9, "5": 13, "6": 15, "7": 17, "8": 19,
  "9": 21, A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21,
  K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14, U: 16,
  V: 10, W: 22, X: 25, Y: 24, Z: 23,
};

/** Valori dei caratteri in posizione pari (1-based) per il check del CF. */
const CF_EVEN: Record<string, number> = {
  "0": 0, "1": 1, "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8,
  "9": 9, A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6, H: 7, I: 8, J: 9, K: 10,
  L: 11, M: 12, N: 13, O: 14, P: 15, Q: 16, R: 17, S: 18, T: 19, U: 20,
  V: 21, W: 22, X: 23, Y: 24, Z: 25,
};

/** Struttura del CF persona fisica, incluse le sostituzioni per omocodia. */
const CF_PERSON_RE =
  /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;

/**
 * Codice fiscale di persona fisica (16 caratteri) con carattere di
 * controllo verificato. Accetta anche i CF numerici a 11 cifre degli enti
 * (equivalenti alla partita IVA) quando `allowNumeric` è true.
 */
export function isValidCodiceFiscale(value: string, allowNumeric = false): boolean {
  const cf = value.toUpperCase();
  if (allowNumeric && /^\d{11}$/.test(cf)) return isValidPartitaIva(cf);
  if (!CF_PERSON_RE.test(cf)) return false;
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const ch = cf[i]!;
    sum += (i + 1) % 2 === 1 ? CF_ODD[ch]! : CF_EVEN[ch]!;
  }
  const check = String.fromCharCode(65 + (sum % 26));
  return cf[15] === check;
}

/** Partita IVA italiana: 11 cifre con cifra di controllo (algoritmo Luhn). */
export function isValidPartitaIva(value: string): boolean {
  const piva = value.replace(/^IT/i, "");
  if (!/^\d{11}$/.test(piva)) return false;
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const digit = Number(piva[i]);
    if (i % 2 === 0) {
      sum += digit; // posizioni dispari (1-based)
    } else {
      const doubled = digit * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    }
  }
  const check = (10 - (sum % 10)) % 10;
  return check === Number(piva[10]);
}

// ── schema del profilo di fatturazione ──────────────────────────────

export const BILLING_PROFILE_KINDS = ["private", "company"] as const;
export type BillingProfileKind = (typeof BILLING_PROFILE_KINDS)[number];

const trimmed = (max: number) => z.string().trim().max(max);
const optionalTrimmed = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .or(z.literal(""))
    .transform((v) => (v ? v : undefined));

export const billingProfileSchema = z
  .object({
    kind: z.enum(BILLING_PROFILE_KINDS),
    firstName: optionalTrimmed(80),
    lastName: optionalTrimmed(80),
    companyName: optionalTrimmed(160),
    address: trimmed(200).min(3),
    zip: trimmed(10).min(2),
    city: trimmed(120).min(2),
    province: optionalTrimmed(60).transform((v) => v?.toUpperCase()),
    country: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/, "codice paese ISO a 2 lettere")
      .default("IT"),
    taxCode: optionalTrimmed(20).transform((v) => v?.toUpperCase()),
    vatNumber: optionalTrimmed(20).transform((v) => v?.toUpperCase()),
    sdiCode: optionalTrimmed(7).transform((v) => v?.toUpperCase()),
    pec: z
      .string()
      .trim()
      .toLowerCase()
      .email()
      .optional()
      .or(z.literal(""))
      .transform((v) => (v ? v : undefined)),
  })
  .superRefine((data, ctx) => {
    const isIt = data.country === "IT";
    const fail = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });

    if (data.kind === "private") {
      if (!data.firstName) fail("firstName", "nome obbligatorio");
      if (!data.lastName) fail("lastName", "cognome obbligatorio");
      if (!data.taxCode) {
        fail("taxCode", "codice fiscale obbligatorio");
      } else if (isIt && !isValidCodiceFiscale(data.taxCode)) {
        fail("taxCode", "codice fiscale non valido");
      }
      if (data.vatNumber && isIt && !isValidPartitaIva(data.vatNumber)) {
        fail("vatNumber", "partita IVA non valida");
      }
    } else {
      if (!data.companyName) fail("companyName", "ragione sociale obbligatoria");
      if (!data.vatNumber) {
        fail("vatNumber", "partita IVA obbligatoria");
      } else if (isIt && !isValidPartitaIva(data.vatNumber)) {
        fail("vatNumber", "partita IVA non valida");
      }
      if (data.taxCode && isIt && !isValidCodiceFiscale(data.taxCode, true)) {
        fail("taxCode", "codice fiscale non valido");
      }
      // fattura elettronica: serve un recapito, codice SDI oppure PEC
      if (isIt && !data.sdiCode && !data.pec) {
        fail("sdiCode", "indicare codice SDI o PEC");
      }
    }

    if (isIt) {
      if (!/^\d{5}$/.test(data.zip)) fail("zip", "CAP a 5 cifre");
      if (!data.province) {
        fail("province", "provincia obbligatoria");
      } else if (!/^[A-Z]{2}$/.test(data.province)) {
        fail("province", "sigla provincia a 2 lettere");
      }
      if (data.sdiCode && !/^[A-Z0-9]{7}$/.test(data.sdiCode)) {
        fail("sdiCode", "codice SDI a 7 caratteri");
      }
    }
  });

export type BillingProfileInput = z.input<typeof billingProfileSchema>;
export type BillingProfile = z.output<typeof billingProfileSchema>;

/** True se i dati passano tutte le regole (profilo pronto per l'acquisto). */
export function isBillingProfileComplete(data: unknown): boolean {
  return billingProfileSchema.safeParse(data).success;
}
